import { useState, useEffect, useMemo } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { rpcCall } from "../ws/client";
import { toast } from "./ui/Toast";
import { Button, Input } from "./ui";
import { useTelegram } from "../providers/TelegramProvider";
import { useNetwork } from "../hooks/useNetwork";
import { getJettonsForNetwork } from "../config/jettons";
import { PaymentDetails } from "./PaymentDetails";
import "../styles/CreateOrder.scss";
import { Link } from "react-router-dom";
import { loadBalanceFromCache, saveBalanceToCache, isRateLimitError } from "../utils/balanceCache";

interface PaymentDetail {
    id: string;
    payment_method: string;
    account_number: string;
    account_name: string;
    bank_name?: string;
    additional_info?: string;
}

interface CreateOrderProps {
    onSuccess?: () => void;
}

export default function CreateOrder({ onSuccess }: CreateOrderProps = {}) {
    const wallet = useTonWallet();
    const { user: tgUser } = useTelegram();
    const { network, loading: networkLoading } = useNetwork();
    
    // Use useMemo to ensure popularJettons updates when network changes
    const popularJettons = useMemo(() => {
        return getJettonsForNetwork(network, true).map(j => ({ // Include native TON for P2P orders
            symbol: j.symbol,
            name: j.name,
            address: j.address,
            decimals: 9 // Default, will be fetched from contract
        }));
    }, [network]);
    
    // Log jettons config for debugging
    useEffect(() => {
        console.log(`[CreateOrder] Current network: ${network}`);
        console.log('[CreateOrder] Jettons config:', popularJettons.map(j => ({ symbol: j.symbol, address: j.address })));
    }, [network, popularJettons]);
    
    const [jettonAddress, setJettonAddress] = useState("");
    const [amount, setAmount] = useState("");
    const [price, setPrice] = useState("");
    const [fiatCurrency, setFiatCurrency] = useState("RUB");
    const [side, setSide] = useState<"buy" | "sell">("sell");
    const [selectedPaymentDetail, setSelectedPaymentDetail] = useState<string>("");
    const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
    const [showAddPaymentDetails, setShowAddPaymentDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [balanceStatus, setBalanceStatus] = useState<{
        hasEnough: boolean | null;
        tonBalance: number;
        jettonBalance: number;
        minTonRequired: number;
        jettonRequired: number;
    } | null>(null);
    const [checkingBalance, setCheckingBalance] = useState(false);
    const [balanceFromCache, setBalanceFromCache] = useState(false);
    const [customJettonInfo, setCustomJettonInfo] = useState<{
        symbol: string;
        name: string;
        decimals: number;
    } | null>(null);
    const [loadingJettonInfo, setLoadingJettonInfo] = useState(false);

    // Загружаем сохранённые платёжные реквизиты
    useEffect(() => {
        loadPaymentDetails();
    }, []);

    const loadPaymentDetails = async () => {
        if (!wallet) return;
        
        try {
            const response = await rpcCall<{}, { payment_details: PaymentDetail[] }>(
                "payment_details.list",
                { wallet: wallet.account.address }
            );
            
            if (response.result?.payment_details) {
                setPaymentDetails(response.result.payment_details);
            }
        } catch (err) {
            console.error("Failed to load payment details:", err);
        }
    };

    const loadJettonInfo = async (address: string) => {
        if (!address || popularJettons.find(j => j.address === address)) {
            setCustomJettonInfo(null);
            return;
        }

        setLoadingJettonInfo(true);
        try {
            const response = await rpcCall<
                { jetton_address: string },
                { success: boolean; symbol: string; name: string; decimals: number; error?: string }
            >(
                "jetton.get_info",
                { jetton_address: address }
            );
            
            if (response.result && response.result.success) {
                setCustomJettonInfo({
                    symbol: response.result.symbol,
                    name: response.result.name,
                    decimals: response.result.decimals
                });
            } else {
                setCustomJettonInfo(null);
                if (response.result?.error) {
                    console.warn("Failed to load jetton info:", response.result.error);
                }
            }
        } catch (err) {
            console.error("Failed to load jetton info:", err);
            setCustomJettonInfo(null);
        } finally {
            setLoadingJettonInfo(false);
        }
    };

    // Reset jettonAddress when network changes (if current address not available in new network)
    useEffect(() => {
        if (!networkLoading && jettonAddress) {
            const jettonStillAvailable = popularJettons.find(j => j.address === jettonAddress);
            if (!jettonStillAvailable) {
                console.log(`🔄 Network changed to ${network}, resetting jetton address (was: ${jettonAddress})`);
                setJettonAddress("");
                setCustomJettonInfo(null);
            }
        }
    }, [network, networkLoading]);

    // Загружаем информацию о custom jetton при изменении адреса
    useEffect(() => {
        // Skip for TON (native) and popular jettons
        if (jettonAddress === "TON") {
            setCustomJettonInfo(null);
            return;
        }
        
        if (jettonAddress && !popularJettons.find(j => j.address === jettonAddress)) {
            // Debounce для избежания множественных запросов при вводе
            const timer = setTimeout(() => {
                loadJettonInfo(jettonAddress);
            }, 500);
            return () => clearTimeout(timer);
        } else {
            setCustomJettonInfo(null);
        }
    }, [jettonAddress]);

    // Проверка баланса при изменении суммы или токена (для sell)
    useEffect(() => {
        if (side === "sell" && jettonAddress && amount && wallet) {
            checkBalance();
        }
    }, [amount, jettonAddress, side, wallet]);

    const checkBalance = async () => {
        if (!jettonAddress || !amount || !wallet) return;
        
        setCheckingBalance(true);
        setBalanceFromCache(false);
        
        try {
            const response = await rpcCall<
                { wallet: string; jetton_address: string; amount: number },
                { can_sell: boolean; ton_balance: number; jetton_balance: number; min_ton_required: number; jetton_required: number; errors: { insufficient_ton: boolean; insufficient_jettons: boolean } }
            >(
                "wallet.check_balance",
                {
                    wallet: wallet.account.address,
                    jetton_address: jettonAddress,
                    amount: parseFloat(amount)
                }
            );
            
            if (response.result) {
                const amountNum = parseFloat(amount);
                const balanceData = {
                    hasEnough: amountNum > 0 ? response.result.can_sell : null,
                    tonBalance: response.result.ton_balance,
                    jettonBalance: response.result.jetton_balance,
                    minTonRequired: response.result.min_ton_required,
                    jettonRequired: response.result.jetton_required
                };
                setBalanceStatus(balanceData);
                
                // Save to cache for future use
                saveBalanceToCache(
                    wallet.account.address,
                    jettonAddress,
                    parseFloat(amount),
                    network,
                    {
                        can_sell: response.result.can_sell,
                        ton_balance: response.result.ton_balance,
                        jetton_balance: response.result.jetton_balance,
                        min_ton_required: response.result.min_ton_required,
                        jetton_required: response.result.jetton_required,
                        errors: response.result.errors
                    }
                );
            } else if (response.error) {
                // Check if it's a rate limit error
                if (isRateLimitError(response.error.message)) {
                    console.warn("⚠️ [CreateOrder] Rate limit error, trying cache...");
                    const cached = loadBalanceFromCache(
                        wallet.account.address,
                        jettonAddress,
                        parseFloat(amount),
                        network
                    );
                    
                    if (cached) {
                        console.log("✅ [CreateOrder] Using cached balance data");
                        const amountNum = parseFloat(amount);
                        setBalanceStatus({
                            hasEnough: amountNum > 0 ? cached.can_sell : null,
                            tonBalance: cached.ton_balance,
                            jettonBalance: cached.jetton_balance,
                            minTonRequired: cached.min_ton_required,
                            jettonRequired: cached.jetton_required
                        });
                        setBalanceFromCache(true);
                        setCheckingBalance(false);
                        return;
                    }
                }
                
                // If balance check fails, allow proceeding but backend will validate
                console.warn("Balance check failed:", response.error.message);
                setBalanceStatus({
                    hasEnough: null, // Unknown - let backend handle validation
                    tonBalance: 0,
                    jettonBalance: 0,
                    minTonRequired: 0,
                    jettonRequired: 0
                });
            }
        } catch (err) {
            console.error("Failed to check balance:", err);
            
            // Try to load from cache on error
            const cached = loadBalanceFromCache(
                wallet.account.address,
                jettonAddress,
                parseFloat(amount),
                network
            );
            
            if (cached) {
                console.log("✅ [CreateOrder] Using cached balance data after error");
                const amountNum = parseFloat(amount);
                setBalanceStatus({
                    hasEnough: amountNum > 0 ? cached.can_sell : null,
                    tonBalance: cached.ton_balance,
                    jettonBalance: cached.jetton_balance,
                    minTonRequired: cached.min_ton_required,
                    jettonRequired: cached.jetton_required
                });
                setBalanceFromCache(true);
            } else {
                // If balance check fails, allow proceeding but backend will validate
                setBalanceStatus({
                    hasEnough: null, // Unknown - let backend handle validation
                    tonBalance: 0,
                    jettonBalance: 0,
                    minTonRequired: 0,
                    jettonRequired: 0
                });
            }
        } finally {
            setCheckingBalance(false);
        }
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};
        
        if (!jettonAddress.trim()) {
            newErrors.jettonAddress = "Выберите или введите токен";
        }
        
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = "Введите корректную сумму";
        }
        
        if (!price || parseFloat(price) <= 0) {
            newErrors.price = "Введите корректную цену";
        }
        
        // Реквизиты нужны только для sell orders (продавец указывает, куда ему платить)
        if (side === "sell" && !selectedPaymentDetail) {
            newErrors.paymentDetail = "Выберите платёжные реквизиты";
        }
        
        // Для sell - проверяем баланс только если введена валидная сумма и баланс известен
        if (side === "sell") {
            const amountNum = parseFloat(amount);
            if (amountNum > 0 && balanceStatus?.hasEnough === false) {
                newErrors.amount = "Недостаточно средств для создания ордера";
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!wallet) {
            toast.warning("Подключите кошелёк");
            return;
        }
        
        if (!validate()) {
            toast.error("Заполните все обязательные поля");
            return;
        }
        
        // Находим символ выбранного токена
        const selectedJetton = popularJettons.find(j => j.address === jettonAddress);
        const jettonSymbol = selectedJetton 
            ? selectedJetton.symbol 
            : customJettonInfo?.symbol || "JETTON";
        
        setLoading(true);
        
        try {
            const response = await rpcCall(
                "orders.create",
                {
                    wallet: wallet.account.address,
                    jetton_address: jettonAddress,
                    jetton_symbol: jettonSymbol,
                    amount: parseFloat(amount),
                    price: parseFloat(price),
                    fiat_currency: fiatCurrency,
                    side,
                    // Реквизиты нужны только для sell orders
                    payment_details_ids: side === "sell" ? [selectedPaymentDetail] : [],
                    tg_user_id: tgUser?.id
                }
            );
            
            if (response.error) {
                toast.error(response.error.message);
            } else {
                toast.success("Ордер создан успешно!");
                // Call onSuccess callback if provided
                if (onSuccess) {
                    onSuccess();
                } else {
                    // Redirect to My Orders with correct basename
                    const basename = '';
                    window.location.href = `${basename}/?tab=my-orders`;
                }
            }
        } catch (err) {
            console.error(err);
            toast.error("Ошибка при создании ордера");
        } finally {
            setLoading(false);
        }
    };

    if (!wallet) {
        return (
            <div className="create-order">
                <div className="wallet-warning">
                    <div className="warning-icon">🔐</div>
                    <div className="warning-title">Подключите кошелёк</div>
                    <div className="warning-text">Чтобы создать ордер, необходимо подключить TON кошелёк</div>
                </div>
            </div>
        );
    }

    return (
        <div className="create-order">
            <div className="form-header">
                <h2>Создать ордер</h2>
                <p>Создайте новый P2P ордер для покупки или продажи токенов</p>
            </div>

            <div className="warning-box">
                <strong>⚠️ Внимание!</strong>
                <p>Все сделки проходят через эскроу. Никогда не отправляйте средства напрямую.</p>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Side Selection */}
                <div className="form-group">
                    <label>Тип операции</label>
                    <div className="side-selector">
                        <button
                            type="button"
                            className={`side-btn ${side === "sell" ? "active" : ""}`}
                            onClick={() => setSide("sell")}
                        >
                            💰 Продать
                        </button>
                        <button
                            type="button"
                            className={`side-btn ${side === "buy" ? "active" : ""}`}
                            onClick={() => setSide("buy")}
                        >
                            🛒 Купить
                        </button>
                    </div>
                </div>

                {/* Jetton Selection */}
                <div className="form-group">
                    <label>Токен</label>
                    <div className="jetton-selector">
                        <div className="jetton-popular">
                            {popularJettons.map(jetton => (
                                <button
                                    key={jetton.symbol}
                                    type="button"
                                    className={`jetton-option ${jettonAddress === jetton.address ? "active" : ""}`}
                                    onClick={() => {
                                        setJettonAddress(jetton.address);
                                    }}
                                >
                                    {jetton.symbol}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className={`jetton-custom-btn ${!popularJettons.find(j => j.address === jettonAddress) && jettonAddress ? "active" : ""}`}
                            onClick={() => setJettonAddress("")}
                        >
                            + Другой токен
                        </button>
                    </div>
                </div>

                {/* Custom Jetton Address (shown when not selecting from popular) */}
                {!popularJettons.find(j => j.address === jettonAddress) && (
                    <>
                        <Input
                            label="Адрес токена (JETTON)"
                            value={jettonAddress}
                            onChange={(e) => setJettonAddress(e.target.value)}
                            placeholder="0:... или EQ..."
                            error={errors.jettonAddress}
                        />
                        
                        {loadingJettonInfo && jettonAddress && (
                            <div className="jetton-info-loading">
                                🔄 Загрузка информации о токене...
                            </div>
                        )}
                        
                        {!loadingJettonInfo && customJettonInfo && jettonAddress && (
                            <div className="jetton-info-display">
                                <div className="jetton-info-row">
                                    <span className="jetton-info-label">Токен:</span>
                                    <strong>{customJettonInfo.symbol}</strong>
                                </div>
                                <div className="jetton-info-row">
                                    <span className="jetton-info-label">Название:</span>
                                    <span>{customJettonInfo.name}</span>
                                </div>
                                <div className="jetton-info-row">
                                    <span className="jetton-info-label">Decimals:</span>
                                    <span>{customJettonInfo.decimals}</span>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Amount */}
                <Input
                    label="Количество токенов"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    error={errors.amount}
                    disabled={side === "sell" && balanceStatus !== null && balanceStatus.hasEnough === false}
                />

                {/* Balance status for sell */}
                {side === "sell" && jettonAddress && amount && (
                    <div className={`balance-status ${checkingBalance ? "checking" : ""} ${balanceStatus?.hasEnough === false ? "error" : ""} ${balanceStatus?.hasEnough === true ? "success" : ""} ${balanceFromCache ? "from-cache" : ""}`}>
                        {checkingBalance ? (
                            <span>🔄 Проверка баланса...</span>
                        ) : balanceStatus ? (
                            balanceStatus.hasEnough === true ? (
                                <>
                                    <span>✅ Баланс достаточен {balanceFromCache && <span className="cache-indicator" title="Данные из кеша (API временно недоступен)">💾</span>}</span>
                                    <small>
                                        {jettonAddress === "TON" ? (
                                            `TON: ${balanceStatus.tonBalance.toFixed(4)}`
                                        ) : (
                                            <>
                                                TON: {balanceStatus.tonBalance.toFixed(4)} | {balanceStatus.jettonBalance.toFixed(2)} {
                                                    popularJettons.find(j => j.address === jettonAddress)?.symbol || 
                                                    customJettonInfo?.symbol || 
                                                    "JETTON"
                                                }
                                                {customJettonInfo?.name && ` (${customJettonInfo.name})`}
                                                {popularJettons.find(j => j.address === jettonAddress) && ` (${popularJettons.find(j => j.address === jettonAddress)?.name})`}
                                            </>
                                        )}
                                    </small>
                                </>
                            ) : balanceStatus.hasEnough === false ? (
                                <>
                                    <span>❌ Недостаточно средств {balanceFromCache && <span className="cache-indicator" title="Данные из кеша (API временно недоступен)">💾</span>}</span>
                                    <small>
                                        {jettonAddress === "TON" ? (
                                            `TON: ${balanceStatus.tonBalance.toFixed(4)} / ${balanceStatus.minTonRequired.toFixed(4)} (${parseFloat(amount).toFixed(4)} + комиссия)`
                                        ) : (
                                            (() => {
                                                const selectedJetton = popularJettons.find(j => j.address === jettonAddress);
                                                const jettonSymbol = selectedJetton?.symbol || customJettonInfo?.symbol || "JETTON";
                                                const errors = [];
                                                // Check what's insufficient
                                                if (balanceStatus.jettonBalance < parseFloat(amount)) {
                                                    errors.push(`${jettonSymbol}: ${balanceStatus.jettonBalance.toFixed(2)} / ${parseFloat(amount).toFixed(2)}`);
                                                }
                                                if (balanceStatus.tonBalance < balanceStatus.minTonRequired) {
                                                    errors.push(`TON для газа: ${balanceStatus.tonBalance.toFixed(4)} / ${balanceStatus.minTonRequired.toFixed(4)}`);
                                                }
                                                return errors.join(' • ');
                                            })()
                                        )}
                                    </small>
                                </>
                            ) : (
                                <>
                                    <span>⚠️ Не удалось проверить баланс</span>
                                    <small>Баланс будет проверен при создании ордера</small>
                                    <button 
                                        type="button" 
                                        className="retry-btn"
                                        onClick={checkBalance}
                                    >
                                        🔄 Повторить
                                    </button>
                                </>
                            )
                        ) : null}
                    </div>
                )}

                {/* Price */}
                <div className="form-row">
                    <Input
                        label="Цена за 1 токен"
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        error={errors.price}
                    />
                    
                    <div className="form-group">
                        <label>Валюта</label>
                        <select 
                            value={fiatCurrency} 
                            onChange={(e) => setFiatCurrency(e.target.value)}
                            className="input-field"
                        >
                            <option value="RUB">RUB (Российский рубль)</option>
                            <option value="USD">USD (Доллар США)</option>
                            <option value="EUR">EUR (Евро)</option>
                            <option value="USDT">USDT</option>
                        </select>
                    </div>
                </div>

                {/* Payment Details - только для sell orders */}
                {side === "sell" && (
                    <div className="form-group">
                        <label>Платёжные реквизиты</label>
                        {errors.paymentDetail && (
                            <span className="error-message">{errors.paymentDetail}</span>
                        )}
                        
                        {paymentDetails.length === 0 ? (
                            <div className="no-payment-details">
                                {!showAddPaymentDetails ? (
                                    <>
                                        <p>У вас нет сохранённых платёжных реквизитов</p>
                                        <br/>
                                        <Link to={`/?tab=payment-details`} className="add-link">
                                            + Добавить
                                        </Link>
                                    </>
                                ) : (
                                    <div className="embedded-payment-details">
                                        <div className="embedded-header">
                                            <h4>Добавить платёжные реквизиты</h4>
                                            <button
                                                type="button"
                                                className="close-btn"
                                                onClick={() => setShowAddPaymentDetails(false)}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <PaymentDetails 
                                            wallet={wallet.account.address}
                                            onDetailsAdded={() => {
                                                loadPaymentDetails();
                                                setShowAddPaymentDetails(false);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="payment-details-list">
                                {paymentDetails.map(detail => (
                                    <div
                                        key={detail.id}
                                        className={`payment-detail-option ${selectedPaymentDetail === detail.id ? "active" : ""}`}
                                        onClick={() => setSelectedPaymentDetail(detail.id)}
                                    >
                                        <div className="detail-method">{detail.payment_method}</div>
                                        <div className="detail-account">
                                            {detail.account_number}
                                            {detail.bank_name && ` • ${detail.bank_name}`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Summary */}
                <div className="order-summary">
                    <div className="summary-row">
                        <span>Итого к получению:</span>
                        <strong>
                            {amount && price 
                                ? (parseFloat(amount) * parseFloat(price)).toFixed(2) 
                                : "0.00"} {fiatCurrency}
                        </strong>
                    </div>
                </div>

                {/* Submit Button */}
                <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg" 
                    block 
                    loading={loading}
                >
                    {side === "sell" ? "Продать" : "Купить"} {amount || "0"} {
                        popularJettons.find(j => j.address === jettonAddress)?.symbol || 
                        customJettonInfo?.symbol || 
                        "JETTON"
                    }
                </Button>
            </form>
        </div>
    );
}
